import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from "react";
import { getCurrentUser, fetchUserAttributes} from 'aws-amplify/auth';
import { Amplify } from 'aws-amplify';
import outputs from '../amplifyconfiguration.json';
import {getUser, createUser, createDefaultDevPlan} from '../api/graphql'

// Set up for accessing the GraphQL API (connecs user info to development plans)
Amplify.configure(outputs);

const MapContext = createContext();

export const MapProvider = ({ children }) => {

    ///// USER DATA /////

    const [userData, setUserData] = useState(null);

    ///// MAP DATA /////

    const [geoJSONData, setGeoJSONData] = useState({
        subcatchments: null, // State for subcatchments GeoJSON data
        outfalls: null,      // State for outfalls GeoJSON data
        overflowData: null,  // State for outfalls overflow data
        avgFlooding: null,   // State for average flooding data
    });

    const [mapClickInfo, setMapClickInfo] = useState({
        selectedLayer: 'subcatchment', // Initialize to subcatchment by default
        assetName: null,     // The name of the map click selected feature
    })

    const [selectedFeature, setSelectedFeature] = useState({ 
        ID: null,   // The ID of the map click selected feature
        Data: null  // The model data for the map click selected feature
    })

    const mapRef = useRef(null);

    ///// VIEW CONTROL /////

    const [layersVisible, setLayersVisible] = useState(true); // The top left button that controls subcatchment colors
    const [colorScheme, setColorScheme] = useState('standard'); // The color scheme for the subcatchment colors

    const [LIDMode, setLIDMode] = useState(true); // The top left button that opens the devPlan panel

    const stormOptions = ['mar27', 'May19', 'Jul18', 'Aug16']; // Removed Sep21 option bc it doesn't look good
    const [selectedStormOption, setSelectedStormOption] = useState('Aug16'); // Chosen storm date range


    const [LIDType, setLIDType] = React.useState(''); // The type of LID feature selected in the LID panel

    const [devPlanID, setDevPlanID] = React.useState('');

    // Structure: {model_name e.g. Aug16}_{user_id}_{dev_plan}.inp
    const [inpFile, setInpFile] = useState('');
    const [outFileExists, setOutFileExists] = useState(false);
    const [planHasBeenRun, setPlanHasBeenRun] = useState(false);
    const [planIsRunning, setPlanIsRunning] = useState(false);
    const [planRunStatus, setPlanRunStatus] = useState({}); // Track which plans have been run

    const [modelDoneRunning, setModelDoneRunning] = useState(false);

    const [stormMode, setStormMode] = useState(true);  // We are moving towards deleting this -- but be a little careful! no breaking!

    const [floodingVisual, setFloodingVisual] = useState(false); // Visualize flooding in nodes

    const [userDevPlansFromS3, setUserDevPlansFromS3] = useState([]); // store all dev plans that has a file in S3 Bucket kensho-results
    const [showFloodMapMsg, setShowFloodMapMsg] = useState(false); // show the "Run the model to view flooding and basement flooding map" message.
    const [loadingFloodMap, setLoadingFloodMap] = useState(false); // show the loading logo when flooding data is loading in

    // Boolean guard for only showing the run model button or progress bar.
    // When set to false: hides the button/progress and shows a loading bar.
    const [runModelGuard, setRunModelGuard] = useState(false);

    const [beforePlan, setBeforePlan] = useState("");
    const [afterPlan, setAfterPlan] = useState("");
    const [beforeStorm, setBeforeStorm] = useState("");
    const [afterStorm, setAfterStorm] = useState("");
    const [runComparison, setRunComparison] = useState(Date.now());

    const [subcatchmentNameJSON, setSubcatchmentNameJSON] = useState({}); // stores the proper subcatchment names

    const [plansStillRunning, setPlansStillRunning] = useState([]);

    const [viewBaseline, setViewBaseline] = useState(false); // view baseline for flooding and basement flooding

    const [editMode, setEditMode] = useState(false);
    const [mouseDown, setMouseDown] = useState(false); // track whether users right click on the map
    const [brushMode, setBrushMode] = useState(false); // brush tool for selecting multiple subcatchments at once
    const [eraserMode, setEraserMode] = useState(false); // eraser tool to erase the brush marks
    const [handMode, setHandMode] = useState(true); // the hand mode for panning the map
    const [polygonMode, setPolygonMode] = useState(false); // New state for polygon drawing mode
    const [addGIFromBrush, setAddGIFromBrush] = useState(false); // trigger the appearance of GI panel when selecting mutliple subcatchments with the brush tool
    const [clearPolygonsTrigger, setClearPolygonsTrigger] = useState(0); // Trigger to clear polygons from any component

    const [progressValue, setProgressValue] = useState(-2);

    const [enableLIDpanel, setEnableLIDpanel] = useState(true);

    // Using this effectively as a semaphore. Functions 'start' and 'finish' increment and decrement the semaphore.
    const [pendingOperations, setPendingOperations] = useState(0);
    const startOperation = useCallback(() => {
        console.log("Start Operation");
        setPendingOperations(prev => prev + 1);
    }, []);

    const finishOperation = useCallback(() => {
        setPendingOperations(prev => Math.max(0, prev - 1));
    }, []);




    ///// USE EFFECTS /////

    // Kaming : added an abort signal to prevent race conditions between API calls and state update.
    useEffect(() => {
        if (!userData || !devPlanID) return;

        // Create an AbortController to cancel the fetch request if devPlanID changes
        const controller = new AbortController();
        const signal = controller.signal;

        const fetchOutFileExists = async () => {
            try {
                startOperation();
                console.log("API Calls with plan : ", devPlanID);
                const url = `https://2471d2z813.execute-api.us-east-2.amazonaws.com/read/${selectedStormOption}/${userData.id}_${devPlanID}/node/049_DRO/total_inflow`;
                const response = await fetch(url, { signal });

                if (!response.ok) throw new Error("Network response was not ok");

                const data = await response.json();
                console.log("Response data:", data);

                if (data && data.x) {
                    setOutFileExists(true);
                    setPlanHasBeenRun(true);
                } else { // Kaming : added an else statement here. Sorry if it breaks anything.
                    setOutFileExists(false);
                    setPlanHasBeenRun(false);
                }
            } catch (error) {
                if (error.name === "AbortError") {
                    console.log("Fetch request was aborted due to rapid state changes.");
                } else {
                    console.error("Error fetching data:", error);
                }
            } finally {
                finishOperation();
            }
        };

        fetchOutFileExists();



        // Cleanup function: abort the previous request when devPlanID changes
        return () => controller.abort();
    }, [userData, devPlanID, selectedStormOption]);


    // This checks if the development plan has been run for any of the storms
    useEffect(() => {
        console.log("$ Current plan: ", devPlanID);

        const checkPlanHasBeenRun = (selectedStorm, user, plan) => {
            const filename = `GLWA_${selectedStorm}_${user.id}_${plan}`;
            return containPlan(selectedStorm, user.id, plan) && !plansStillRunning.includes(filename);
        }

        const checkAllPlans = async () => {
            if (!userData?.developmentPlans?.items) return;
            try {
                startOperation();
                const newPlanRunStatus = {};
                for (const plan of userData.developmentPlans.items) {
                    // const hasBeenRun = await checkPlanHasBeenRun(userData, plan.id);
                    const hasBeenRun = checkPlanHasBeenRun(selectedStormOption, userData, plan.id);
                    newPlanRunStatus[plan.id] = hasBeenRun;
                }
                setPlanRunStatus(newPlanRunStatus);

                // Also update planHasBeenRun for the current plan
                if (devPlanID in newPlanRunStatus) {
                    console.log("Set Plan Has Been Run : ", newPlanRunStatus[devPlanID]);
                    setPlanHasBeenRun(newPlanRunStatus[devPlanID]);
                }
            } finally {
                finishOperation();
            }

        }
        setRunModelGuard(false);
        if (userData) {
            checkAllPlans();

        }
        setRunModelGuard(true);
    }, [userData, devPlanID, selectedStormOption]);


    // Populates the userData state with the user's ID based on the login email
    // and creates a new user in the GraphQL database if this is the first login
    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const { username: userId } = await getCurrentUser();
                const attributes = await fetchUserAttributes();
                const email = attributes.email;
                const data = await getUser(userId);
                if (!data) { // Something went wrong.
                    setUserData({ id: userId, email: email });
                    console.error('Error fetching user data:', data);
                    return;
                }
                if (data.getUser !== null) { // Returning user.
                    setUserData(data.getUser);
                    checkAndCreateDevelopmentPlan(data.getUser); // Check this just in case.
                    return;
                }

                // New user -- create a new user in the GraphQL database
                const newUserData = await createUser(userId, email);
                if (!newUserData) {
                    setUserData({ id: userId, email: email });
                    return;
                }
                setUserData(newUserData.data.createUser);
                startOperation();
                checkAndCreateDevelopmentPlan(newUserData.data.createUser);

            } catch (error) {
                console.error('Error in user authentication:', error);
                setUserData(null);
            } finally {
                finishOperation();
            }
        };

        fetchUserData();
    }, []); // Run once upon loading


    // stores all proper subcatchment names
    useEffect(() => {
        fetch("/Subcatchment_names/subcatchmentNames.json")
            .then(response => response.json())
            .then(data => setSubcatchmentNameJSON(data))
            .catch(error => console.error("Error loading subcatchment names:", error));
    }, []);
    
    const checkAndCreateDevelopmentPlan = async (newUserData) => {
        console.log("CHECK THE DEVELOPMENT PLANS - DO WE CREATE?")
        if (!newUserData || !newUserData.developmentPlans) {
            console.error('Cannot create development plan: User data is missing');
            return;
        }
        if (!newUserData.developmentPlans.items || newUserData.developmentPlans.items.length === 0) {
            try {
                const devPlans = newUserData.developmentPlans?.items;
                if (devPlans && devPlans.length > 0) {
                    console.log('We see userData devPlans:', devPlans);
                } else {
                    console.log('No development plans found');
                    createDefaultDevelopmentPlan(newUserData);
                }
            } catch (error) {
                console.error('Error fetching user data:', error);
            }
        } else {
            if (!userData) return;
            const devPlans = userData.developmentPlans?.items;
            if (devPlans && devPlans.length > 0) {
                console.log('LID Panel sees userData:', devPlans);
            } else { // I don't think this else statement should ever run -- 
                console.log('No development plans found');
                console.log("This else statement shouldn't have run - go find it in the code!")
                createDefaultDevelopmentPlan(newUserData);
            }
        }
    };
    
    const createDefaultDevelopmentPlan = async (newUserData) => {
        console.log("WERE CREATING A DEV PLAN AND THE USER DATA IS : ", newUserData);
        if (!newUserData.id) {
            console.error('Cannot create development plan: User ID is missing');
            return;
        }
        if (newUserData.developmentPlans && newUserData.developmentPlans.items.length > 0) {
            console.error('Cannot create template development plan: There are already development plans');
            return;
        }
        if (newUserData.developmentPlans && newUserData.developmentPlans.items.some(plan => plan.name === "myFirstDevPlan")) {
            console.error('Cannot create template development plan: myFirstDevPlan already exists');
            return;
        }
        try {
            const result = await createDefaultDevPlan(newUserData.id);
            setDevPlanID(result.data.createDevelopmentPlan.id);
            setUserData(prevData => ({ ...prevData, developmentPlans: { items: [...prevData.developmentPlans.items, result.data.createDevelopmentPlan] } }));
        } catch (error) {
            console.error('Error creating development plan:', error);
        }
    };

    // Sets the selected feature ID and data when the map is clicked
    const handleMapClick = (event, feat = null) => {
        console.log('Map clicked:', event);

        const features = feat ? feat : mapRef.current.queryRenderedFeatures(event.point, {
            layers: ['subcatchments', 'outfalls']
        });

        if (features.length > 0) {
            const clickedFeature = features[0];
            setEnableLIDpanel(true);
            setSelectedFeature(prevFeat => ({ ...prevFeat, ID: clickedFeature.properties.NAME }));
            setMapClickInfo(prevInfo => ({ ...prevInfo, assetName: clickedFeature.properties.NAME }));
            setSelectedFeature(prevFeat => ({ ...prevFeat, Data: clickedFeature.properties}));
            if ('AREA' in clickedFeature.properties) {
                setMapClickInfo(prevInfo => ({ ...prevInfo, selectedLayer: 'subcatchment' }));
            } else {
                setMapClickInfo(prevInfo => ({ ...prevInfo, selectedLayer: 'node' }));
            }
            
        } else {
            setSelectedFeature(prevFeat => ({ ...prevFeat, ID: null }));
            setSelectedFeature(prevFeat => ({ ...prevFeat, Data: null }));
            setLIDType('');
        }
    };

    // Closes the draggable panel when the close button is clicked
    const handleCloseDraggable = () => {
        setSelectedFeature(prevFeat => ({ ...prevFeat, ID: null, Data: null }));
        //setTimeSeries({ timeseriesData: null, timeseriesSum: null, rainfallData: null, rainfallSum: null });
    };


    const handleCloseLIDPanel = () => {
        setEnableLIDpanel(false);
    };

    // Toggle the visibility of the storm mode panel
    const handleStormMode = () => {
        setStormMode(!stormMode);
    };

    // Given a storm, user_id and plan id, check if the corresponding file exists in the state userDevPlansFromS3 which grabs all files from S3, kensho-results.
    const containPlan = (storm, user_id, plan) => {
        const match_filename = `GLWA_${storm}_${user_id}_${plan}`;
        return userDevPlansFromS3.some(devPlan => devPlan === match_filename);
    };


    const value = {
        geoJSONData,
        setGeoJSONData,
        mapClickInfo,
        setMapClickInfo,

        layersVisible,
        setLayersVisible,
        colorScheme,
        setColorScheme,

        selectedFeature,
        setSelectedFeature,
        stormMode,
        setStormMode,
        LIDMode,
        setLIDMode,
        LIDType,
        setLIDType,
        // LIDContent,
        // setLIDContent,

        floodingVisual,
        setFloodingVisual,

        mapRef,

        stormOptions,
        selectedStormOption,
        setSelectedStormOption,

        handleMapClick,
        handleCloseDraggable,
        handleStormMode,
        handleCloseLIDPanel,
        // handleLIDMode,

        userData,
        setUserData,
        devPlanID,
        setDevPlanID,
        inpFile,
        setInpFile,
        outFileExists,
        setOutFileExists,
        planHasBeenRun,
        setPlanHasBeenRun,
        planIsRunning,
        setPlanIsRunning,

        planRunStatus,
        setPlanRunStatus,

        userDevPlansFromS3, 
        setUserDevPlansFromS3,

        showFloodMapMsg, 
        setShowFloodMapMsg,

        containPlan,

        loadingFloodMap, 
        setLoadingFloodMap,

        beforePlan,
        setBeforePlan,
        afterPlan,
        setAfterPlan,
        beforeStorm,
        setBeforeStorm,
        afterStorm,
        setAfterStorm,

        runComparison,
        setRunComparison,

        runModelGuard,
        setRunModelGuard,

        plansStillRunning, 
        setPlansStillRunning,

        viewBaseline, 
        setViewBaseline,

        editMode, 
        setEditMode,
        mouseDown, 
        setMouseDown,
        brushMode, 
        setBrushMode,
        eraserMode, 
        setEraserMode,
        subcatchmentNameJSON,
        setSubcatchmentNameJSON,

        addGIFromBrush, 
        setAddGIFromBrush,

        handMode,
        setHandMode,
        polygonMode,
        setPolygonMode,
        clearPolygonsTrigger,
        setClearPolygonsTrigger,

        modelDoneRunning, 
        setModelDoneRunning,
        progressValue, 
        setProgressValue,

        enableLIDpanel,
        setEnableLIDpanel,
        pendingOperations,
        startOperation,
        finishOperation,
    }

    return (
        <MapContext.Provider value={value}>
            {children}
        </MapContext.Provider>
    );
};

export const useMapContext = () => {
    const context = useContext(MapContext);
    if (!context) {
        throw new Error("useMapContext must be used within a MapProvider");
    }
    return context;
}